import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { LOCALE_PREFIXES } from "@/i18n";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/craftfindus-logo.svg";

function redirectTo(navigate: ReturnType<typeof useNavigate>, prefix: string) {
  localStorage.setItem("craftfindus_locale_prefix", prefix);
  navigate(`/${prefix}/`, { replace: true });
}

function detectFromBrowser(): string | null {
  const browserLang = (navigator.language ?? "").toLowerCase();
  if (!browserLang) return null;
  const exact = LOCALE_PREFIXES.find(
    (p) =>
      p.i18nCode.toLowerCase() === browserLang ||
      p.locale.toLowerCase() === browserLang
  );
  if (exact) return exact.prefix;
  const base = browserLang.split("-")[0];
  const baseMatch = LOCALE_PREFIXES.find((p) =>
    p.i18nCode.toLowerCase().startsWith(base)
  );
  return baseMatch?.prefix ?? null;
}

export default function LanguageSelector() {
  const navigate = useNavigate();
  const [suggested, setSuggested] = useState<string | null>(null);
  const [detecting, setDetecting] = useState(true);

  useEffect(() => {
    const isBot = /bot|crawl|spider|slurp|googlebot|bingbot/i.test(
      navigator.userAgent
    );
    if (isBot) { setDetecting(false); return; }

    // 1. Returning users
    const saved = localStorage.getItem("craftfindus_locale_prefix");
    if (saved && LOCALE_PREFIXES.find((p) => p.prefix === saved)) {
      redirectTo(navigate, saved);
      return;
    }

    // 2. Try IP-based geo detection, then browser language, then show picker
    (async () => {
      try {
        const { data } = await supabase.functions.invoke("geo-detect");
        if (data?.prefix) {
          const valid = LOCALE_PREFIXES.find((p) => p.prefix === data.prefix);
          if (valid) {
            redirectTo(navigate, valid.prefix);
            return;
          }
        }
      } catch { /* ignore */ }

      // 3. Browser language fallback
      const browserPrefix = detectFromBrowser();
      if (browserPrefix) {
        redirectTo(navigate, browserPrefix);
        return;
      }

      // 4. Show manual picker as last resort
      setSuggested(detectFromBrowser());
      setDetecting(false);
    })();
  }, [navigate]);

  const handleSelect = (prefix: string) => {
    localStorage.setItem("craftfindus_locale_prefix", prefix);
    navigate(`/${prefix}/`);
  };

  if (detecting) {
    return (
      <main className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <img src={logo} alt="CRAFTFINDUS" className="h-20 md:h-24 mb-6 animate-pulse" />
        <p className="text-muted-foreground text-sm">Erkennung läuft…</p>
      </main>
    );
  }

  return (
    <>
      <Helmet>
        <title>CRAFTFINDUS | Choose your country</title>
        <meta name="robots" content="noindex, follow" />
        <meta
          name="description"
          content="CRAFTFINDUS – Select your country and language to find qualified craft businesses."
        />
      </Helmet>
      <main className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <img src={logo} alt="CRAFTFINDUS" className="h-20 md:h-24 mb-10" />
        <h1 className="text-xl md:text-2xl font-semibold mb-2 text-foreground text-center">
          Wählen Sie Ihr Land / Choose your country
        </h1>
        <p className="text-muted-foreground text-sm mb-8 text-center">
          Selecteer uw land · Choisissez votre pays · Wybierz swój kraj
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-w-3xl w-full">
          {LOCALE_PREFIXES.map((lp) => (
            <button
              key={lp.prefix}
              onClick={() => handleSelect(lp.prefix)}
              className={`flex items-center gap-3 px-4 py-3 border rounded-lg transition-colors text-left ${
                suggested === lp.prefix
                  ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                  : "border-border hover:bg-accent"
              }`}
            >
              <span className="text-2xl">{lp.flag}</span>
              <span className="text-sm font-medium text-foreground">
                {lp.label}
              </span>
            </button>
          ))}
        </div>
        {suggested && (
          <p className="mt-6 text-sm text-muted-foreground">
            ↑ Vorgeschlagen basierend auf Ihren Browsereinstellungen
          </p>
        )}
      </main>
    </>
  );
}
